import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { FiSun, FiMoon, FiRotateCcw } from 'react-icons/fi';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('hero');
    const { language, t, setLanguage } = useLanguage();
    const { theme, toggleTheme, isDark } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            setIsScrolled(scrollPosition > 100);

            // Solo detectar sección activa en la página principal
            if (location.pathname === '/') {
                const sections = ['hero', 'about', 'services', 'contact'];
                const navbarHeight = 80;
                const currentSection = sections.find(section => {
                    const element = document.getElementById(section);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        return rect.top <= navbarHeight + 50 && rect.bottom >= navbarHeight + 50;
                    }
                    return false;
                });

                if (currentSection) {
                    setActiveSection(currentSection);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location.pathname]);

    const handleNavigation = (sectionId) => {
        if (location.pathname === '/') {
            // Si estamos en la página principal, hacer scroll
            scrollToSection(sectionId);
        } else {
            // Si estamos en otra página, navegar a la principal y luego hacer scroll
            navigate('/');
            setTimeout(() => {
                scrollToSection(sectionId);
            }, 100);
        }
        setIsMenuOpen(false);
    };

    const handlePageNavigation = (path) => {
        navigate(path);
        setIsMenuOpen(false);
    };

    const handleLogoClick = () => {
        if (location.pathname === '/') {
            // Si estamos en la página principal, hacer scroll hacia arriba
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            setActiveSection('hero');
        } else {
            // Si estamos en otra página, navegar a la principal
            navigate('/');
        }
        setIsMenuOpen(false);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
        setIsMenuOpen(false);
        setActiveSection(sectionId);
    }; return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
            ? `${isDark ? 'bg-black/95' : 'bg-white/95'} shadow-2xl border-b-2 ${isDark ? 'border-atlantis' : 'border-green-yellow-primary'}`
            : `${isDark ? 'bg-black/90' : 'bg-white/90'} backdrop-blur-md shadow-xl border-b-2 ${isDark ? 'border-gray-700/50' : 'border-green-yellow-200/50'}`
            }`}>
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isScrolled ? 'py-2' : 'py-3'
                }`}>
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <div className="flex-shrink-0 flex items-center space-x-3">
                        <button
                            type="button"
                            aria-label="NJ Design & Print - Home"
                            className="flex items-center space-x-3 cursor-pointer group bg-transparent hover:bg-transparent border-0 p-0"
                            onClick={handleLogoClick}
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-atlantis to-green-yellow-primary rounded-full blur-sm opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
                                <img
                                    src="/logo.jpg"
                                    alt="NJ Design & Print"
                                    className="relative h-12 w-12 object-cover rounded-full border-2 border-atlantis shadow-lg group-hover:scale-110 transition-transform duration-300"
                                />
                            </div>
                            <span className={`hidden sm:block text-xl font-bold transition-all duration-300 group-hover:scale-105 ${isDark
                                ? 'bg-gradient-to-r from-green-yellow-neon to-atlantis bg-clip-text text-transparent'
                                : 'text-black'
                                }`}>
                                NJ Design & Print
                            </span>
                        </button>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center space-x-8">
                        <div className="flex items-baseline space-x-6">
                            <button
                                onClick={() => handleNavigation('hero')}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 relative group cursor-pointer ${(activeSection === 'hero' && location.pathname === '/')
                                    ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                                    : `${isDark
                                        ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                        : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                                    }`
                                    }`}
                            >
                                {t.home}
                                {activeSection === 'hero' && location.pathname === '/' && (
                                    <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                                )}
                            </button>
                            <button
                                onClick={() => handleNavigation('about')}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 relative group cursor-pointer ${(activeSection === 'about' && location.pathname === '/')
                                    ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                                    : `${isDark
                                        ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                        : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                                    }`
                                    }`}
                            >
                                {t.about}
                                {activeSection === 'about' && location.pathname === '/' && (
                                    <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                                )}
                            </button>
                            <button
                                onClick={() => handlePageNavigation('/portfolio')}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 relative group cursor-pointer ${location.pathname === '/portfolio'
                                    ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                                    : `${isDark
                                        ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                        : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                                    }`
                                    }`}
                            >
                                {t.portfolio || 'Portafolio'}
                                {location.pathname === '/portfolio' && (
                                    <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                                )}
                            </button>
                            <button
                                onClick={() => handlePageNavigation('/services')}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 relative group cursor-pointer ${location.pathname === '/services'
                                    ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                                    : `${isDark
                                        ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                        : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                                    }`
                                    }`}
                            >
                                {t.services}
                                {location.pathname === '/services' && (
                                    <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                                )}
                            </button>
                            <button
                                onClick={() => handlePageNavigation('/contact')}
                                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 relative group cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 ${location.pathname === '/contact'
                                    ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                                    : `${isDark
                                        ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                        : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                                    }`
                                    }`}
                            >
                                {t.contact}
                                {activeSection === 'contact' && (
                                    <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                                )}
                            </button>
                        </div>

                        {/* Language and Theme Toggles */}
                        <div className="flex items-center space-x-4 ml-6">
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className={`p-3 rounded-lg transition-all duration-300 border-2 shadow-lg hover:scale-110 ${isDark
                                    ? 'bg-gray-800 text-green-yellow-neon hover:bg-gray-700 border-atlantis/30 hover:border-atlantis'
                                    : 'bg-white text-atlantis hover:bg-gray-50 border-atlantis/30 hover:border-atlantis'
                                    }`}
                                title={theme === 'light' ? t.lightMode : theme === 'dark' ? t.darkMode : t.autoMode}
                            >
                                {theme === 'light' ? (
                                    <FiSun className="w-5 h-5" />
                                ) : theme === 'dark' ? (
                                    <FiMoon className="w-5 h-5" />
                                ) : (
                                    <FiRotateCcw className="w-5 h-5" />
                                )}
                            </button>

                            {/* Language Toggle */}
                            <div className={`flex items-center space-x-1 p-1 rounded-lg border-2 ${isDark
                                ? 'bg-gray-800 border-atlantis/30'
                                : 'bg-white border-atlantis/30'
                                }`}>
                                <button
                                    onClick={() => setLanguage('es')}
                                    className={`px-3 py-2 text-xs font-bold rounded-md transition-all duration-300 ${language === 'es'
                                        ? 'bg-gradient-to-r from-atlantis to-green-yellow-primary text-black shadow-md'
                                        : `${isDark
                                            ? 'text-gray-300 hover:text-green-yellow-neon hover:bg-gray-700'
                                            : 'text-atlantis hover:text-black hover:bg-gray-100'
                                        }`
                                        }`}
                                >
                                    ES
                                </button>
                                <div className={`w-px h-4 ${isDark ? 'bg-gray-600' : 'bg-green-yellow-primary/30'}`}></div>
                                <button
                                    onClick={() => setLanguage('en')}
                                    className={`px-3 py-2 text-xs font-bold rounded-md transition-all duration-300 ${language === 'en'
                                        ? 'bg-gradient-to-r from-atlantis to-green-yellow-primary text-black shadow-md'
                                        : `${isDark ? 'text-gray-300 hover:text-green-yellow-neon hover:bg-gray-700' : 'text-atlantis hover:text-black hover:bg-gray-100'}`
                                        }`}
                                >
                                    EN
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden flex items-center space-x-3">
                        {/* Mobile Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className={`p-2 rounded-lg transition-all duration-300 ${isDark
                                ? 'bg-gray-800 text-green-yellow-neon border border-atlantis/30'
                                : 'bg-black text-green-yellow-primary border border-green-yellow-primary/30'
                                }`}
                        >
                            {theme === 'light' ? (
                                <FiSun className="w-4 h-4" />
                            ) : theme === 'dark' ? (
                                <FiMoon className="w-4 h-4" />
                            ) : (
                                <FiRotateCcw className="w-4 h-4" />
                            )}
                        </button>

                        {/* Mobile Language Toggle */}
                        <div className={`flex items-center space-x-1 p-1 rounded-lg ${isDark
                            ? 'bg-gray-800 border border-atlantis/30'
                            : 'bg-black border border-green-yellow-primary/30'
                            }`}>
                            <button
                                onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                                className={`px-2 py-1 text-xs font-bold rounded ${language === 'es'
                                    ? 'text-green-yellow-neon'
                                    : isDark ? 'text-green-yellow-neon' : 'text-green-yellow-primary'
                                    }`}
                            >
                                {language.toUpperCase()}
                            </button>
                        </div>

                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className={`inline-flex items-center justify-center p-2 rounded-lg transition-all duration-300 ${isDark
                                ? 'text-green-yellow-neon hover:text-white hover:bg-gray-800 border border-atlantis/30'
                                : 'text-black hover:text-white hover:bg-atlantis border border-green-yellow-primary/30'
                                } focus:outline-none focus:ring-2 focus:ring-atlantis`}
                        >
                            <span className="sr-only">{language === 'es' ? 'Abrir menú principal' : 'Open main menu'}</span>
                            {/* Icon when menu is closed */}
                            <svg
                                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6 transition-transform duration-300`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            {/* Icon when menu is open */}
                            <svg
                                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6 transition-transform duration-300 rotate-180`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            <div className={`${isMenuOpen ? 'block animate-slide-down' : 'hidden'} md:hidden ${isDark ? 'bg-black/95 border-atlantis/30' : 'bg-white/95 border-green-yellow-primary/30'
                } backdrop-blur-md border-t-2`}>
                <div className="px-4 pt-4 pb-4 space-y-2">
                    <button
                        onClick={() => handleNavigation('hero')}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 relative ${(activeSection === 'hero' && location.pathname === '/')
                            ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                            : `${isDark
                                ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                            }`
                            }`}
                    >
                        {t.home}
                        {activeSection === 'hero' && location.pathname === '/' && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                        )}
                    </button>
                    <button
                        onClick={() => handleNavigation('about')}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 relative ${(activeSection === 'about' && location.pathname === '/')
                            ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                            : `${isDark
                                ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                            }`
                            }`}
                    >
                        {t.about}
                        {activeSection === 'about' && location.pathname === '/' && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                        )}
                    </button>
                    <button
                        onClick={() => handlePageNavigation('/portfolio')}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 relative ${location.pathname === '/portfolio'
                            ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                            : `${isDark
                                ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                            }`
                            }`}
                    >
                        {t.portfolio || 'Portafolio'}
                        {location.pathname === '/portfolio' && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                        )}
                    </button>
                    <button
                        onClick={() => handlePageNavigation('/services')}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 relative ${location.pathname === '/services'
                            ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                            : `${isDark
                                ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                            }`
                            }`}
                    >
                        {t.services}
                        {location.pathname === '/services' && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                        )}
                    </button>
                    <button
                        onClick={() => handlePageNavigation('/contact')}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 relative ${location.pathname === '/contact'
                            ? 'text-black bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-green-yellow-primary hover:to-atlantis'
                            : `${isDark
                                ? 'text-gray-300 hover:text-black hover:bg-gradient-to-r hover:from-green-yellow-primary hover:to-atlantis'
                                : 'text-black hover:text-black hover:bg-gradient-to-r hover:from-atlantis hover:to-green-yellow-neon'
                            }`
                            }`}
                    >
                        {t.contact}
                        {activeSection === 'contact' && (
                            <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-atlantis rounded-full shadow-lg"></span>
                        )}
                    </button>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
